import { COUNTRY_CODES } from './countryCodes';

export const MELBET_EXTRA_SLUG = 'melbet-promo-code-2021-enter-mainbonus-and-get-130-deposit-bonus';

// A bonus with `country` (ISO code) is matched by it; legacy ones without it by a slug keyword.
// Extra (global) bonuses fill in only for bookmakers that have no country bonus on the hub.
export const filterBonusesForCountry = (
  bonuses,
  filterKeyword,
  { countryCode, extraSlugs = [MELBET_EXTRA_SLUG] } = {}
) => {
  if (!Array.isArray(bonuses)) return [];

  const matchesCountry = (bonus) => {
    if (bonus.country) return Boolean(countryCode) && bonus.country === countryCode;
    return bonus.slug.toLowerCase().includes(filterKeyword);
  };
  const brand = (bonus) => String(bonus.bookmaker || '').toLowerCase();
  const withSlug = bonuses.filter((bonus) => bonus?.slug);
  const localBookmakers = new Set(
    withSlug.filter((bonus) => !extraSlugs.includes(bonus.slug) && matchesCountry(bonus)).map(brand).filter(Boolean)
  );

  return withSlug.filter((bonus) =>
    extraSlugs.includes(bonus.slug) ? !localBookmakers.has(brand(bonus)) : matchesCountry(bonus)
  );
};

export const countryBonuses = [
  {
    id: 'bangladesh',
    route: '/bonuses-bangladesh',
    seoKey: 'bonusesBangladesh',
    filterKeyword: 'bangladesh',
    content: {
      title: 'Bangladesh Betting Bonuses',
      subtitle: 'Welcome offers for BD players. Terms compared side by side',
      emptyMessage: 'No specific bonuses for Bangladesh found at the moment.',
      footerTitle: 'Top Betting Bonuses for Bangladesh Players',
      footerBody:
        "In Bangladesh a ৳10,000 first deposit lands exactly on the ৳13,000 cap shared by the three one-deposit offers here: 1xBet with BONUSMB, Melbet with MAINBONUS and Betwinner with EUR100. Smaller deposits get the same 130% match. The bonus is staked five times in accumulators within 30 days. BPL and international cricket can fill slips, but Bangladesh as a heavy favourite is often priced under 1.40, which matters most at Melbet, where every leg must clear that price. 1win (BONUSMAIN) spreads its welcome package over four deposits, with each step's cap in the cashier, and BC.Game (4d41hebza) pays its bonus on a crypto deposit.",
      faq: [
        {
          q: "Which promo codes work in Bangladesh?",
          a: "BONUSMB at 1xBet, MAINBONUS at Melbet and EUR100 at Betwinner, each 130% of the first deposit up to ৳13,000. 1win uses BONUSMAIN and BC.Game 4d41hebza; their bonus amounts are shown in the account after sign-up.",
        },
        {
          q: "What deposit reaches the ৳13,000 cap?",
          a: "৳10,000. A larger first deposit earns no extra bonus.",
        },
        {
          q: "Can I deposit with bKash or Nagad?",
          a: "Check each bookmaker's cashier after registering: the wallets available for BDT accounts are listed there with their minimums.",
        },
        {
          q: "Do BPL bets count towards the bonus?",
          a: "At 1xBet, Melbet and Betwinner, yes, inside an accumulator; a BPL favourite priced under 1.40 does not count as a qualifying leg. 1win and BC.Game set their own rules in the bonus terms.",
        },
      ],
      actionLabel: 'Bangladesh betting sites',
      actionTo: '/best-betting-sites/bangladesh',
    }
  },
  {
    id: 'india',
    route: '/bonuses-india',
    seoKey: 'bonusesIndia',
    filterKeyword: 'india',
    content: {
      title: 'India Betting Bonuses',
      subtitle: 'Welcome bonuses, UPI-friendly bookmakers, and how to get started',
      emptyTitle: 'No bonuses available right now',
      emptyMessage: "We're working on bringing you the best betting bonuses for India.",
      showEmptyRefresh: true,
      footerTitle: 'Cricket & Sports Betting Bonuses for Indian Players',
      footerBody:
        "The cricket format matters for these bonuses: T20 and ODI legs settle the same day, while a Test match can keep a slip open for five days of the 30-day window. 1xBet (BONUSMB), Melbet (MAINBONUS) and Betwinner (EUR100) each match the first deposit 130%, up to ₹33,000 on an INR account, and the bonus is staked five times in accumulators. At Melbet every leg must be priced 1.40 or more; the other two need three such legs.",
      faq: [
        {
          q: "Which promo codes work in India?",
          a: "BONUSMB at 1xBet, MAINBONUS at Melbet and EUR100 at Betwinner, each 130% of the first deposit up to ₹33,000.",
        },
        {
          q: "Do IPL bets count towards the bonus?",
          a: "Yes, as legs of a qualifying accumulator. IPL matches settle the same day, which keeps the wagering moving.",
        },
        {
          q: "What deposit gets the full ₹33,000?",
          a: "About ₹25,400. Smaller deposits get the same 130% match.",
        },
        {
          q: "Do Test match bets count?",
          a: "Yes, but a Test leg keeps the slip open until the match ends, which can take five days.",
        },
      ],
      actionLabel: 'India betting sites',
      actionTo: '/best-betting-sites/india',
    }
  },
  {
    id: 'pakistan',
    route: '/bonuses-pakistan',
    seoKey: 'bonusesPakistan',
    filterKeyword: 'pakistan',
    content: {
      title: 'Pakistan Betting Bonuses',
      subtitle: 'Cricket and sports promos for Pakistani bettors',
      emptyMessage: 'No specific bonuses for Pakistan found at the moment.',
      footerTitle: 'Cricket & Sports Betting Offers for Pakistani Players',
      footerBody:
        "If you bet from a phone, 1xBet, Melbet and Betwinner accept their promo code in the Android app's sign-up form: BONUSMB at 1xBet, MAINBONUS at Melbet and EUR100 at Betwinner. Each lifts the first-deposit match from 100% to 130%, up to Rs 20,000 on a PKR account. The rollover is the same everywhere, 5x in accumulators within 30 days. Where they differ is the slip rule: 1xBet and Betwinner need three legs at 1.40 or more, Melbet needs every leg at that price, which matters for short-priced cricket favourites. Two apps newly added here work differently: 1win (BONUSMAIN) splits its welcome package over four deposits, and BC.Game (4d41hebza) takes crypto deposits such as USDT.",
      faq: [
        {
          q: "Which betting apps in Pakistan give a bonus on registration?",
          a: "1xBet, Melbet and Betwinner: register with BONUSMB, MAINBONUS or EUR100 and your first deposit is matched 130%, up to Rs 20,000. 1win (BONUSMAIN) and BC.Game (4d41hebza) also take a code at sign-up. Every bonus arrives with a deposit, not at registration.",
        },
        {
          q: "Is there a betting app in Pakistan with a bonus without investment?",
          a: "Not among these offers: each needs a first deposit. Apps that promise free money for signing up and then ask for a payment to activate it are a common scam.",
        },
        {
          q: "Can I deposit with JazzCash or Easypaisa?",
          a: "Check the cashier of a PKR account at each bookmaker. The available wallets and their minimums are listed there.",
        },
        {
          q: "How much can I get with a promo code?",
          a: "At 1xBet, Melbet and Betwinner, up to Rs 20,000, reached with a first deposit of about Rs 15,400. 1win and BC.Game show their bonus sizes in the account after sign-up.",
        },
        {
          q: "Do cricket bets count towards the bonus?",
          a: "At 1xBet, Melbet and Betwinner, yes, as legs of an accumulator; singles, including PSL singles, do not count. At 1win and BC.Game, check the eligible bets in the bonus terms.",
        },
      ],
      actionLabel: 'Pakistan betting apps',
      actionTo: '/best-betting-sites/pakistan',
    }
  },
  {
    id: 'nepal',
    route: '/bonuses-nepal',
    seoKey: 'bonusesNepal',
    filterKeyword: 'nepal',
    content: {
      title: 'Nepal Betting Bonuses',
      subtitle: 'NPR welcome offers for EPL and NPL cricket fans',
      emptyMessage: 'No specific bonuses for Nepal found at the moment.',
      footerTitle: 'Welcome Bonuses for Nepali Players',
      footerBody:
        "Up to a first deposit of about NPR 13,080 all three offers here pay the same 130%; above that only 1xBet's NPR 22,000 cap keeps growing. Each bonus is paid in rupees on an NPR account. Register at 1xBet with BONUSMB for 130% of the first deposit up to NPR 22,000, or at Melbet with MAINBONUS and Betwinner with EUR100 for 130% up to NPR 17,000. Each bonus is staked five times in accumulators within 30 days, and Melbet requires every leg at odds of 1.40 or more. Open the cashier before your first deposit and use only the methods it lists.",
      faq: [
        {
          q: "Which promo codes work in Nepal?",
          a: "BONUSMB at 1xBet, MAINBONUS at Melbet and EUR100 at Betwinner, each entered on the registration form. Each raises the first-deposit match from 100% to 130%.",
        },
        {
          q: "Which Nepal betting bonus is the biggest?",
          a: "1xBet: 130% up to NPR 22,000 with BONUSMB. Melbet and Betwinner pay 130% up to NPR 17,000, so below a first deposit of about NPR 13,080 all three give the same amount.",
        },
        {
          q: "Is there a free bonus on registration in Nepal?",
          a: "Not among these offers. The bonus is paid on top of your first deposit, so NPR 2,000 brings NPR 2,600 of bonus.",
        },
        {
          q: "What deposit gets the full bonus?",
          a: "About NPR 16,925 at 1xBet and about NPR 13,080 at Melbet and Betwinner. Pay only through methods listed in the cashier, never to a personal wallet number sent in a message.",
        },
        {
          q: "Do NPL cricket bets count towards the bonus?",
          a: "Yes, as legs of a qualifying accumulator. At Melbet every leg must be priced 1.40 or more.",
        },
      ],
      actionLabel: 'Nepal betting sites',
      actionTo: '/best-betting-sites/nepal',
    }
  },
  {
    id: 'nigeria',
    route: '/bonuses-nigeria',
    seoKey: 'bonusesNigeria',
    filterKeyword: 'nigeria',
    content: {
      title: 'Nigeria Betting Bonuses',
      subtitle: 'Naira welcome offers with clear wagering terms',
      emptyMessage: 'No specific bonuses for Nigeria found at the moment.',
      footerTitle: 'Top Naira Betting Bonuses for Nigerian Punters',
      footerBody:
        "Open your account in naira, not dollars, and the welcome offers here are worth up to ₦300,000 without converting every deposit. 1xBet (BONUSMB), Melbet (MAINBONUS) and Betwinner (EUR100) each match the first deposit 130%. The bonus is staked five times in accumulators within 30 days. Long accumulators count, but short slips of three or four legs are more likely to clear the wagering.",
      faq: [
        {
          q: "Which betting sites in Nigeria give a welcome bonus?",
          a: "1xBet, Melbet and Betwinner, each 130% of the first deposit up to ₦300,000 on an NGN account with their promo codes.",
        },
        {
          q: "Should I open my account in naira or dollars?",
          a: "For these bonuses, naira. The cap and the deposits are then in NGN, with no conversion.",
        },
        {
          q: "Do long accumulators count?",
          a: "Yes, but they lose more often. Three or four legs are more likely to clear the wagering than long ones.",
        },
        {
          q: "What deposit gets the full ₦300,000?",
          a: "About ₦230,800. Smaller deposits get the same 130% match.",
        },
      ],
      actionLabel: 'Nigeria betting sites',
      actionTo: '/best-betting-sites/nigeria',
    }
  },
  {
    id: 'kenya',
    route: '/bonuses-kenya',
    seoKey: 'bonusesKenya',
    filterKeyword: 'kenya',
    content: {
      title: 'Kenya Betting Bonuses',
      subtitle: 'M-Pesa friendly welcome offers for Kenyan bettors',
      emptyMessage: 'No specific bonuses for Kenya found at the moment.',
      footerTitle: 'M-Pesa Betting Bonuses for Kenyan Players',
      footerBody:
        "Karibu bonus is a common Kenyan name for a welcome offer on the first deposit. At 1xBet, Melbet and Betwinner it is 130% of that deposit, up to KES 20,000, when you register with BONUSMB, MAINBONUS or EUR100. M-Pesa is the usual way to fund a KES account, so check that it is listed in each cashier. The bonus is staked five times in accumulators within 30 days. Melbet's rule is the strictest: every leg at 1.40 or more.",
      faq: [
        {
          q: "Which betting sites have a karibu bonus?",
          a: "1xBet, Melbet and Betwinner. Karibu means welcome, and their welcome offers match 130% of the first deposit, up to KES 20,000, with a promo code.",
        },
        {
          q: "Which betting sites in Kenya give a registration bonus?",
          a: "The same three. The bonus is paid when you make the first deposit, not when you register.",
        },
        {
          q: "Are there free bets in Kenya without a deposit?",
          a: "Not among these offers. A small first deposit still counts: KES 1,000 brings KES 1,300 of bonus.",
        },
        {
          q: "Can I deposit with M-Pesa?",
          a: "M-Pesa is the usual method for KES accounts. Check each cashier for the minimum.",
        },
      ],
      actionLabel: 'Kenya betting sites',
      actionTo: '/best-betting-sites/kenya',
    }
  },
  {
    id: 'cameroon',
    route: '/bonuses-cameroon',
    seoKey: 'bonusesCameroon',
    filterKeyword: 'cameroon',
    content: {
      title: 'Cameroon Betting Bonuses',
      subtitle: 'XAF welcome offers and mobile money promotions',
      emptyMessage: 'No specific bonuses for Cameroon found at the moment.',
      footerTitle: 'Football Betting Bonuses for Cameroonian Players',
      footerBody:
        "All three sites on this page can be used in French, and their welcome bonuses are paid in francs CFA. Register at 1xBet with BONUSMB, at Melbet with MAINBONUS or at Betwinner with EUR100, and the first deposit is matched 130%, up to 130,000 XAF. Check each cashier for MTN Mobile Money and Orange Money. The bonus is staked five times in accumulators over 30 days, and at Melbet every leg must be priced 1.40 or more.",
      faq: [
        {
          q: "Which betting apps in Cameroon have a welcome bonus?",
          a: "1xBet, Melbet and Betwinner. With their promo codes each matches 130% of the first deposit, up to 130,000 XAF, and all three apps can be used in French.",
        },
        {
          q: "Which promo codes work in Cameroon?",
          a: "BONUSMB at 1xBet, MAINBONUS at Melbet and EUR100 at Betwinner, each entered on the registration form.",
        },
        {
          q: "What deposit gets the full 130,000 XAF?",
          a: "100,000 XAF. Smaller deposits get the same 130% match.",
        },
        {
          q: "Can I bet in French?",
          a: "Yes. Switch the site or app language to French before you register.",
        },
      ],
      actionLabel: 'Cameroon betting sites',
      actionTo: '/best-betting-sites/cameroon',
    }
  },
  {
    id: 'ethiopia',
    route: '/bonuses-ethiopia',
    seoKey: 'bonusesEthiopia',
    filterKeyword: 'ethiopia',
    content: {
      title: 'Ethiopia Betting Bonuses',
      subtitle: 'ETB welcome offers for Ethiopian sports bettors',
      emptyMessage: 'No specific bonuses for Ethiopia found at the moment.',
      footerTitle: 'Sports Betting Bonuses for Ethiopian Players',
      footerBody:
        "The 1xBet, Melbet and Betwinner bonuses here are paid on your first deposit, in birr. 1xBet (code BONUSMB), Melbet (MAINBONUS) and Betwinner (EUR100) match that deposit 130% instead of the standard 100%, up to ETB 5,000. The bonus then has to be staked five times in accumulators within 30 days before it can be withdrawn. Melbet is the strictest of the three, with every leg at odds of 1.40 or more. Two newer sites sit beside them: 1win (BONUSMAIN), whose package covers four deposits, and BC.Game (4d41hebza) for crypto players. Ethio betting bonuses that promise free birr for signing up, and ask for money to unlock them, are not offered by any of these bookmakers.",
      faq: [
        {
          q: "Is there a 100 birr free bonus on registration in Ethiopia?",
          a: "Not from the bookmakers listed here. Their registration bonuses are paid on top of a first deposit. Offers that ask you to send money to a phone number to unlock a free bonus are scams.",
        },
        {
          q: "What does a 100% registration bonus mean?",
          a: "Your first deposit is matched in full: deposit ETB 1,000 and receive ETB 1,000 of bonus. With these promo codes the match is 130%.",
        },
        {
          q: "Which betting site gives a free bet on registration without deposit in Ethiopia?",
          a: "None of the sites here. Their offers are deposit bonuses; at 1xBet, Melbet or Betwinner a deposit of ETB 1,000 brings ETB 1,300 with a promo code.",
        },
        {
          q: "Are there new betting sites in Ethiopia with a registration bonus?",
          a: "Yes: 1win (BONUSMAIN) and BC.Game (4d41hebza) are the newest on this page. Before registering at any new site, check that the bonus terms are published on the bookmaker's own site and that it never asks you to pay to unlock a bonus.",
        },
        {
          q: "Which sites give the biggest registration bonus?",
          a: "1xBet, Melbet and Betwinner each give up to ETB 5,000 on the first deposit with their promo codes. 1win advertises up to 500% spread over four deposits; check the cap for each step in its cashier.",
        },
      ],
      actionLabel: 'Ethiopia betting sites',
      actionTo: '/best-betting-sites/ethiopia',
    }
  },
  {
    id: 'ghana',
    route: '/bonuses-ghana',
    seoKey: 'bonusesGhana',
    filterKeyword: 'ghana',
    content: {
      title: 'Ghana Betting Bonuses',
      subtitle: 'GHS offers with mobile money and free bet deals',
      emptyMessage: 'No specific bonuses for Ghana found at the moment.',
      footerTitle: 'Mobile Money Betting Bonuses for Ghanaian Players',
      footerBody:
        "Searching for a free bet in Ghana? The offers on this page are deposit bonuses instead: they add bonus money after your first deposit rather than giving you a free stake. 1xBet (BONUSMB), Melbet (MAINBONUS) and Betwinner (EUR100) match that deposit 130%, up to GHS 550, and the bonus is staked five times in accumulators within 30 days. Fund the account from a mobile money wallet in your own name, because payouts are sent back to it.",
      faq: [
        {
          q: "Which betting companies in Ghana give a free bet?",
          a: "The offers here are deposit bonuses rather than free bets: 1xBet, Melbet and Betwinner each match 130% of the first deposit, up to GHS 550.",
        },
        {
          q: "How small can my first deposit be?",
          a: "Any deposit at or above the bonus minimum shown in the cashier gets 130%. GHS 50 brings GHS 65 of bonus.",
        },
        {
          q: "Which mobile money wallets can I use?",
          a: "Check the cashier of a GHS account: the wallets available, such as MTN MoMo, are listed there with their minimums.",
        },
        {
          q: "What deposit gets the full GHS 550?",
          a: "About GHS 423.",
        },
      ],
      actionLabel: 'Ghana betting sites',
      actionTo: '/best-betting-sites/ghana',
    }
  },
  {
    id: 'philippines',
    route: '/bonuses-philippines',
    seoKey: 'bonusesPhilippines',
    filterKeyword: 'philippines',
    content: {
      title: 'Philippines Betting Bonuses',
      subtitle: 'PHP welcome offers for GCash and e-wallet users',
      emptyMessage: 'No specific bonuses for Philippines found at the moment.',
      footerTitle: 'Online Betting Bonuses for Filipino Players',
      footerBody:
        "NBA and PBA spreads are priced around 1.90 on both sides, which makes basketball an easy way to build qualifying slips for these offers. 1xBet with BONUSMB and Betwinner with EUR100 pay 130% of the first deposit in pesos, up to PHP 6,000, with a 5x accumulator rollover over 30 days. Melbet's card here shows its global offer, set in your account currency. Check each cashier for GCash or Maya.",
      faq: [
        {
          q: "Which promo codes work in the Philippines?",
          a: "BONUSMB at 1xBet and EUR100 at Betwinner, each up to PHP 6,000. Melbet's MAINBONUS works too, with the cap shown in its cashier.",
        },
        {
          q: "Do basketball bets count towards the bonus?",
          a: "Yes, inside an accumulator. NBA and PBA spreads around 1.90 qualify comfortably.",
        },
        {
          q: "What deposit gets the full PHP 6,000?",
          a: "About PHP 4,615.",
        },
        {
          q: "Can I deposit with GCash?",
          a: "Check the cashier of a PHP account. The e-wallets available are listed there with their minimums.",
        },
      ],
    }
  },
  {
    id: 'zambia',
    route: '/bonuses-zambia',
    seoKey: 'bonusesZambia',
    filterKeyword: 'zambia',
    // Reachable by direct URL and sitemap only: left out of site-wide country lists
    unlisted: true,
    content: {
      title: 'Zambia Betting Bonuses',
      subtitle: 'ZMW welcome offers for EPL and Super League fans',
      emptyMessage: 'No specific bonuses for Zambia found at the moment.',
      footerTitle: 'Welcome Bonuses for Zambian Players',
      footerBody:
        "1xBet (BONUSMB) matches the first deposit 130% up to ZMW 6,300; Melbet (MAINBONUS) and Betwinner (EUR100) pay 130% up to ZMW 5,000. The gap matters only above a first deposit of about ZMW 3,850. Each bonus must be wagered five times in accumulators within 30 days. At 1xBet and Betwinner three legs must be priced 1.40 or more; at Melbet every leg must be, which matters when Power Dynamos or ZESCO are short-priced at home. Open a ZMW account and check the cashier for your mobile money network before you deposit.",
      faq: [
        {
          q: "Which promo codes work in Zambia?",
          a: "BONUSMB at 1xBet, MAINBONUS at Melbet and EUR100 at Betwinner, each entered on the registration form. Each raises the first-deposit match from 100% to 130%.",
        },
        {
          q: "Which betting bonus in Zambia is the biggest?",
          a: "1xBet: 130% up to ZMW 6,300. Melbet and Betwinner pay up to ZMW 5,000, so below a first deposit of about ZMW 3,850 all three give the same amount.",
        },
        {
          q: "Is there a free bonus on registration in Zambia?",
          a: "Not among these offers. The bonus is paid on top of your first deposit, so ZMW 500 brings ZMW 650 of bonus. Anyone asking for a payment to unlock a free bonus is running a scam.",
        },
        {
          q: "What deposit reaches the cap?",
          a: "About ZMW 4,850 at 1xBet and about ZMW 3,850 at Melbet and Betwinner. Pay only through methods listed in the cashier, never to a personal number sent in a chat.",
        },
        {
          q: "Do Super League bets count towards the bonus?",
          a: "Yes, as legs of a qualifying accumulator. Singles do not count, and at Melbet every leg must be priced 1.40 or more.",
        },
      ],
      actionLabel: 'Zambia betting sites',
      actionTo: '/best-betting-sites/zambia',
    }
  },
  {
    id: 'tanzania',
    route: '/bonuses-tanzania',
    seoKey: 'bonusesTanzania',
    filterKeyword: 'tanzania',
    content: {
      title: 'Tanzania Betting Bonuses',
      subtitle: 'TZS welcome offers with M-Pesa and mobile money',
      emptyMessage: 'No specific bonuses for Tanzania found at the moment.',
      footerTitle: 'Sports Betting Bonuses for Tanzanian Players',
      footerBody:
        "Simba and Young Africans dominate Tanzanian betting slips, but against weaker sides their prices often sit too low to count towards a welcome bonus. That rule decides how you use the offers here. 1xBet (BONUSMB), Melbet (MAINBONUS) and Betwinner (EUR100) each match the first deposit 130%, up to TZS 540,000, with a 5x accumulator rollover over 30 days. At 1xBet and Betwinner three legs must be priced 1.40 or more; at Melbet every leg must be.",
      faq: [
        {
          q: "Which betting sites in Tanzania have a welcome bonus?",
          a: "1xBet, Melbet and Betwinner, each up to TZS 540,000 with their promo codes. The bonus is credited after your first deposit.",
        },
        {
          q: "Is there a free bet in Tanzania without a deposit?",
          a: "Not among the offers here. They are first-deposit bonuses.",
        },
        {
          q: "Can I use Simba and Young Africans matches for the bonus?",
          a: "Yes, as accumulator legs. When they are priced under 1.40 they do not count as one of the qualifying legs, and at Melbet they make the slip not count at all.",
        },
        {
          q: "Can I deposit with M-Pesa or Airtel Money?",
          a: "Check the cashier of a TZS account at each bookmaker for the wallets on offer and their minimums.",
        },
        {
          q: "What deposit gets the full TZS 540,000?",
          a: "About TZS 415,400. Any smaller first deposit gets the same 130% match.",
        },
      ],
      actionLabel: 'Tanzania betting sites',
      actionTo: '/best-betting-sites/tanzania',
    }
  },
  {
    id: 'uganda',
    route: '/bonuses-uganda',
    seoKey: 'bonusesUganda',
    filterKeyword: 'uganda',
    content: {
      title: 'Uganda Betting Bonuses',
      subtitle: 'UGX welcome offers with mobile money support',
      emptyMessage: 'No specific bonuses for Uganda found at the moment.',
      footerTitle: 'Mobile Money Betting Bonuses for Ugandan Players',
      footerBody:
        "Uganda's caps look large in shillings: up to UGX 1,250,000 at 1xBet, Melbet and Betwinner when you register with BONUSMB, MAINBONUS or EUR100. You do not have to deposit anywhere near that to benefit, because the 130% match works the same on a UGX 50,000 first deposit. Each bonus carries a 5x rollover in accumulators over 30 days. Melbet counts a slip only when every leg is priced 1.40 or more; 1xBet and Betwinner need three such legs.",
      faq: [
        {
          q: "Which betting companies in Uganda give a welcome bonus?",
          a: "1xBet, Melbet and Betwinner. With their promo codes each matches 130% of the first deposit, up to UGX 1,250,000, paid in shillings.",
        },
        {
          q: "Is there a free bet on registration in Uganda?",
          a: "The offers here are deposit bonuses: they add bonus money after your first deposit. Registration alone does not credit anything.",
        },
        {
          q: "Can I deposit with MTN Mobile Money or Airtel Money?",
          a: "Mobile money is the usual way to fund a shilling account. Each bookmaker's cashier lists the wallets available and their minimums.",
        },
        {
          q: "How much do I need to deposit to get the welcome bonus?",
          a: "Any first deposit above the bonus minimum gets 130%. About UGX 961,540 reaches the full UGX 1,250,000.",
        },
      ],
      actionLabel: 'Uganda betting sites',
      actionTo: '/best-betting-sites/uganda',
    }
  }
];

export const getCountryBonusByRoute = (pathname) =>
  countryBonuses.find((entry) => entry.route === pathname);

/** Hubs shown in site-wide country lists; `unlisted` ones are reachable by URL and sitemap only. */
export const listedCountryBonuses = countryBonuses.filter((entry) => !entry.unlisted);

const UNLISTED_COUNTRY_CODES = new Set(
  countryBonuses.filter((entry) => entry.unlisted).map((entry) => COUNTRY_CODES[entry.id])
);

/** Site-wide bonus lists leave out the bonuses of unlisted countries; their own hub still shows them. */
export const withoutUnlistedCountries = (bonuses) =>
  bonuses.filter((bonus) => !UNLISTED_COUNTRY_CODES.has(bonus.country));
